import { CellProps, NodeValues } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "../types"
import { queryCache, useQuery } from "react-query"

import { FieldsKey } from "../components/AutoCompute/types"
import { ListCell } from "./ListCell"
import { fetchCopyQualities } from "api/copyQualities"
import { fetchPlanCopies } from "api/plans"
import { get } from "lodash"
import { isSemanticReadonly } from "../utils"
import { set } from "lodash/fp"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"

export function CopyCell(props: CellProps) {
  const { rowId: id, columnId, dataKey, primaryKey, semantic, disabled } = props
  const { id: planId } = useParams<{ id: string }>()
  const defaultFieldIds = useDefaultFieldIds(id)
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const writeTo = tableSource.state

  const type = readFrom[id]?.type as PlanRecordType
  const packaged = readFrom[id]?.packaged
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const readonly =
    isSemanticReadonly(type, primaryKey as FieldsKey, semantic, packaged) ||
    disabled

  const action = readFrom[id]?.action
  const selected = get(readFrom[id], `${dataKey}.id`) as string
  const updatable = !action || action === "UPDATE_ORDER"
  const mediaTypeSemanticCode = readFrom[id]?.ancestorMediaSemanticCode

  const params = new URLSearchParams()
  mediaTypeSemanticCode &&
    params.set("mediaTypeSemanticCode", mediaTypeSemanticCode)

  const { data: copies } = useQuery(
    ["copies", planId, params.toString()],
    fetchPlanCopies,
    {
      enabled:
        !!mediaTypeSemanticCode &&
        !queryCache.getQueryData(["copies", planId, params.toString()]),
    },
  )

  const { data: copyQualities } = useQuery(
    "copy-qualities",
    fetchCopyQualities,
    {
      enabled: !queryCache.getQueryData("copy-qualities"),
    },
  )

  const options = copies?.map((c) => ({
    value: c.id,
    label: c.name,
    disabled: c.status === "INACTIVE",
  }))

  // apply billing type rules
  const viewable = useBillingTypesRules(
    readFrom[id] as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable || isMediaSubmedia) return <span />

  const bulkDependencies = (
    row: NodeValues<ChangedPlanRecord>,
    value?: any | null,
  ) => {
    const {
      duration,
      language,
      copyQuality: copyQualityId,
      copyFactor,
      rateCardCost,
    } = defaultFieldIds || {}

    const updatedMediaFields: PlanRecord["planningMediaFields"] =
      row?.[primaryKey as "planningMediaFields"] || {}

    // always nullify the rate card cost
    updatedMediaFields[rateCardCost] = ""

    // if there is a selected copy
    if (!!value?.id) {
      // write the selected copy
      updatedMediaFields[columnId] = value

      // get the selected copy resource
      const copy = copies?.find((c) => c.id === value.id)

      // populate the fields
      if (!!duration) updatedMediaFields[duration] = copy?.duration ?? ""
      if (!!language) updatedMediaFields[language] = copy?.language || ""
      if (!!copyQualityId)
        updatedMediaFields[copyQualityId] = !!copy?.copyQualityId
          ? { id: copy?.copyQualityId }
          : null

      // get the copy quality based on the copy quality id
      const copyQuality = copyQualities?.find(
        (cq) => cq.id === copy?.copyQualityId,
      )
      // update the copy factor based on the copy quality
      if (!!copyFactor)
        updatedMediaFields[copyFactor] = copyQuality?.factor ?? ""

      return set(primaryKey, updatedMediaFields, row)
    }
    // if the copy is cleared
    else {
      // empty the selected copy and the populated fields
      updatedMediaFields[columnId] = null
      !!copyQualityId && (updatedMediaFields[copyQualityId] = null)
      !!duration && (updatedMediaFields[duration] = null)
      !!language && (updatedMediaFields[language] = null)
      !!copyFactor && (updatedMediaFields[copyFactor] = null)

      return set(primaryKey, updatedMediaFields, row)
    }
  }

  const onChange = (value?: string) => {
    writeTo[id] = bulkDependencies(writeTo[id], { id: value })

    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  return (
    <ListCell
      {...props}
      bulkDependencies={bulkDependencies}
      options={options}
      onChange={onChange}
      selected={selected}
      disabled={readonly}
      dataKey={dataKey}
    />
  )
}
